import {
  Box,
  Button,
  Column,
  Flex,
  FormField,
  InputPasswordField,
  OTPInput,
  Row,
  Typography,
} from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { type KeyboardEvent } from 'react';
import { FormProvider, useFormContext } from 'react-hook-form';
import { AuthErrorBanner } from '../../shared';
import { PasswordRequirements } from './PasswordRequirements';
import {
  OTP_LENGTH,
  useResetPasswordForm,
  useResetPasswordFormLogic,
  type IResetPasswordStepValues,
} from './useResetPasswordForm';

const FORM_WIDTH = 528;
const BUTTON_WIDTH = 296;
const BUTTON_RADIUS = 999;

export interface IResetPasswordStepProps {
  identifier: string;
  onResetSuccess: () => void;
}

const activateOnSpaceOrEnter = (handler: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    handler();
  }
};

export const ResetPasswordStep = ({ identifier, onResetSuccess }: IResetPasswordStepProps) => {
  const { methods } = useResetPasswordForm();

  return (
    <FormProvider {...methods}>
      <ResetPasswordStepInner identifier={identifier} onResetSuccess={onResetSuccess} />
    </FormProvider>
  );
};

const ResetPasswordStepInner = ({ identifier, onResetSuccess }: IResetPasswordStepProps) => {
  const { register } = useFormContext<IResetPasswordStepValues>();
  const {
    submit,
    bannerRef,
    banners,
    showBannerArea,
    otp,
    otpError,
    newPwdError,
    confirmPwdError,
    isResetting,
    isResending,
    handleOtpChange,
    handleResend,
  } = useResetPasswordFormLogic({ identifier, onResetSuccess });

  return (
    <form onSubmit={submit} data-testid="reset-password-form" noValidate>
      <Column align="center">
        <Typography
          as="h1"
          size="heading_2"
          bold
          textAlign="center"
          color={colors.neutral[600]}
        >
          You&apos;re Almost Back In
        </Typography>

        <Box style={{ marginTop: 16 }}>
          <Typography size="body_large" color={colors.neutral[500]} textAlign="center">
            Enter the 5-digit code sent to {identifier} and set your new password to continue.
          </Typography>
        </Box>
      </Column>

      <Flex justify="center">
        <Box style={{ width: FORM_WIDTH }}>
          <Box
            ref={bannerRef}
            style={{ marginTop: showBannerArea ? 24 : 0 }}
            data-testid="reset-password-banner-anchor"
          >
            <Column>
              {banners.map((banner) => (
                <AuthErrorBanner
                  key={banner.key}
                  visible
                  message={banner.message}
                  prefix={banner.prefix}
                />
              ))}
            </Column>
          </Box>

          <Flex justify="center" style={{ marginTop: showBannerArea ? 16 : 32 }}>
            <OTPInput
              data-cy="input-otp"
              length={OTP_LENGTH}
              value={otp}
              onChange={handleOtpChange}
              error={otpError}
              autoFocus
            />
          </Flex>

          <Row justify="center" style={{ marginTop: 12 }}>
            <Typography
              as="span"
              bold
              color={colors.secondary[500]}
              style={{
                fontSize: 18,
                cursor: isResending ? 'default' : 'pointer',
                opacity: isResending ? 0.6 : 1,
              }}
              role="button"
              tabIndex={0}
              data-cy="resend-code-link"
              aria-busy={isResending}
              aria-disabled={isResending}
              onClick={handleResend}
              onKeyDown={activateOnSpaceOrEnter(handleResend)}
            >
              {isResending ? 'Sending…' : 'Resend Code'}
            </Typography>
          </Row>

          <Box style={{ marginTop: 32 }}>
            <FormField label="New Password">
              <InputPasswordField
                data-cy="input-new-password"
                placeholder="Enter your new password"
                isError={newPwdError}
                {...register('newPassword')}
              />
            </FormField>
          </Box>

          <Box style={{ marginTop: 16 }}>
            <PasswordRequirements />
          </Box>

          <Box style={{ marginTop: 24 }}>
            <FormField label="Confirm New Password">
              <InputPasswordField
                data-cy="input-confirm-password"
                placeholder="Confirm your new password"
                isError={confirmPwdError}
                {...register('confirmPassword')}
              />
            </FormField>
          </Box>

          <Flex justify="center" style={{ marginTop: 48 }}>
            <Button
              data-cy="btn-reset-password"
              type="submit"
              variant="primary"
              size="lg"
              isLoading={isResetting}
              style={{ width: BUTTON_WIDTH, borderRadius: BUTTON_RADIUS }}
            >
              Reset Password
            </Button>
          </Flex>
        </Box>
      </Flex>
    </form>
  );
};
